// Keeps the free gift in the cart whenever any product from the
// "Performance Inspired Range - Buy 1 Get Free Gift" collection is present.
// The free gift is added with a `_free_gift` line-item property so we can
// tell it apart from a copy of the same variant the customer added themselves.
//
// Invariant (kept reactive after every cart change):
//   - if >= 1 collection product ("trigger") is in the cart  -> exactly one
//     free gift line must be present  (re-added if removed or if trigger
//     quantity is reduced but still >= 1)
//   - if 0 triggers are in the cart -> the free gift line is removed (so a
//     lone "free" gift is never charged)
// A trigger is any collection product line that is NOT our `_free_gift` line
// (so the gift itself, added by the customer, also counts as a trigger and
// yields a BOGO free copy).
//
// The set of collection variant IDs is provided by the theme as
// `window.WW_PERF_INSPIRED_VARIANTS` (rendered in layout/theme.liquid), so
// this works on the collection page, product pages, and quick view.
//
// Triggers: the theme's `product-ajax:added` event (instant add on add) and
// the `cartUpdate` pub/sub event (reactive after any add/remove/qty change).
// Reconciliation uses the cart object carried by the `cartUpdate` event
// (fresh from the server) rather than re-fetching, to avoid acting on a
// cached/stale cart snapshot.
(function () {
  'use strict';

  var GIFT_VARIANT_ID = 60714724327794;
  var PUB_SUB = window.FoxTheme && window.FoxTheme.pubsub;
  var CART_UPDATE = PUB_SUB && PUB_SUB.PUB_SUB_EVENTS && PUB_SUB.PUB_SUB_EVENTS.cartUpdate;

  function isCollectionVariant(vid) {
    var list = window.WW_PERF_INSPIRED_VARIANTS;
    if (!list || !list.length) return false;
    for (var i = 0; i < list.length; i++) {
      if (Number(list[i]) === vid) return true;
    }
    return false;
  }

  var giftAddInProgress = false;

  function cartAddUrl() {
    if (window.FoxTheme && FoxTheme.routes && FoxTheme.routes.cart_add_url) {
      return FoxTheme.routes.cart_add_url;
    }
    return (window.Shopify && Shopify.routes ? Shopify.routes.root : '/') + '/cart/add.js';
  }

  function cartChangeUrl() {
    if (window.FoxTheme && FoxTheme.routes && FoxTheme.routes.cart_change_url) {
      return FoxTheme.routes.cart_change_url;
    }
    return (window.Shopify && Shopify.routes ? Shopify.routes.root : '/') + '/cart/change.js';
  }

  function cartUrl() {
    if (window.FoxTheme && FoxTheme.routes && FoxTheme.routes.cart_url) {
      return FoxTheme.routes.cart_url;
    }
    return (window.Shopify && Shopify.routes ? Shopify.routes.root : '/') + '/cart.js';
  }

  function fetchConfig() {
    if (window.FoxTheme && FoxTheme.utils && FoxTheme.utils.fetchConfig) {
      return FoxTheme.utils.fetchConfig();
    }
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    };
  }

  function getCart() {
    return fetch(cartUrl(), { ...fetchConfig(), method: 'GET', cache: 'no-cache' })
      .then(function (response) {
        return response.json();
      })
      .catch(function () {
        return { items: [] };
      });
  }

  // Collect the section IDs the theme needs re-rendered (mirrors ProductForm).
  function collectSections() {
    var sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );
    return sectionsToBundle;
  }

  // Re-render the cart drawer immediately using the sections HTML that
  // cart/add.js returns when we pass `sections` (this is exactly what
  // ProductForm does after a successful add, which is why the drawer
  // updates instantly instead of only after a manual page refresh).
  function refreshCartAfterAdd(parsedState) {
    return getCart().then(function (cartJson) {
      if (!cartJson) return;
      var sections = parsedState['sections'] || {};
      cartJson['sections'] = sections;
      if (Object.keys(sections).length && PUB_SUB) {
        // Primary path: re-render drawer from the sections HTML returned by
        // cart/add.js (generated server-side at add time, so it already
        // includes the gift — no race, no manual refresh needed).
        PUB_SUB.publish(CART_UPDATE, { cart: cartJson });
      } else {
        // Fallback: ask the drawer to re-fetch its section from the server.
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: false } }));
      }
      return cartJson;
    });
  }

  // Add the gift variant via cart/add.js (mirrors ProductForm's AJAX add).
  function addGiftNow() {
    var sectionsToBundle = collectSections();
    var config = fetchConfig();
    config.body = JSON.stringify({
      items: [{ id: GIFT_VARIANT_ID, quantity: 1, properties: { _free_gift: 'true' } }],
      sections: sectionsToBundle,
      sections_url: window.location.pathname,
    });
    if (window.console) console.log('[auto-add-free-gift] adding free gift variant', GIFT_VARIANT_ID);
    return fetch(cartAddUrl(), config).then(function (response) {
      if (!response.ok) {
        throw new Error('cart/add status ' + response.status);
      }
      return response.json();
    });
  }

  // Adds the free gift line (assumes the caller has already confirmed it is
  // missing). Lock-guarded so concurrent triggers don't create duplicates.
  function addFreeGiftLine() {
    if (giftAddInProgress) return;
    giftAddInProgress = true;
    if (window.console) console.log('[auto-add-free-gift] adding free gift line');
    addGiftNow()
      .then(function (parsedState) {
        return refreshCartAfterAdd(parsedState);
      })
      .catch(function (error) {
        if (window.console) console.error('[auto-add-free-gift] add failed:', error);
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: false } }));
      })
      .finally(function () {
        giftAddInProgress = false;
      });
  }

  // Add-on-add path: a collection product was just added, so ensure the free
  // gift exists (fresh server check to avoid duplicate lines).
  function ensureGiftInCart() {
    if (giftAddInProgress) return;
    giftAddInProgress = true;

    getCart()
      .then(function (cart) {
        var present = cart && cart.items && cart.items.some(function (item) {
          if (Number(item.variant_id) !== GIFT_VARIANT_ID && Number(item.id) !== GIFT_VARIANT_ID) return false;
          var props = item.properties || {};
          return !!props['_free_gift'];
        });
        if (present) {
          if (window.console) console.log('[auto-add-free-gift] free gift already in cart, skipping');
          return;
        }
        return addGiftNow();
      })
      .then(function (parsedState) {
        if (!parsedState) return;
        return refreshCartAfterAdd(parsedState);
      })
      .catch(function (error) {
        if (window.console) console.error('[auto-add-free-gift] failed:', error);
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: false } }));
      })
      .finally(function () {
        giftAddInProgress = false;
      });
  }

  // Remove our free gift line (used when no trigger remains, so it is never
  // charged). `lineItem` is a cart item carrying its 1-based `line` index.
  function removeFreeGift(lineItem) {
    if (giftAddInProgress) return;
    var lineIndex = lineItem.line;
    if (!lineIndex) return;
    var config = fetchConfig();
    config.body = JSON.stringify({ line: lineIndex, quantity: 0 });
    if (window.console) console.log('[auto-add-free-gift] removing free gift line', lineIndex);
    fetch(cartChangeUrl(), config)
      .then(function (response) {
        return response.json();
      })
      .then(function (cartJson) {
        if (PUB_SUB) {
          PUB_SUB.publish(CART_UPDATE, { cart: cartJson });
        } else {
          document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: false } }));
        }
      })
      .catch(function (error) {
        if (window.console) console.error('[auto-add-free-gift] remove failed:', error);
      });
  }

  // Reconcile the free gift line against the current cart: add it when a
  // trigger is present and it is missing, remove it when no trigger remains.
  // `cart` is the fresh cart object from the `cartUpdate` event (or a load
  // fetch), so no extra request is needed.
  function reconcileFromCart(cart) {
    if (!cart || !cart.items) return;
    if (!window.WW_PERF_INSPIRED_VARIANTS || !window.WW_PERF_INSPIRED_VARIANTS.length) return;

    var triggerCount = 0;
    var freeGiftLine = null;

    cart.items.forEach(function (item) {
      var vid = Number(item.variant_id || item.id);
      var props = item.properties || {};
      var isFreeGift = vid === GIFT_VARIANT_ID && !!props['_free_gift'];
      if (isFreeGift) {
        freeGiftLine = item;
        return;
      }
      if (isCollectionVariant(vid)) {
        triggerCount += 1;
      }
    });

    if (triggerCount >= 1 && !freeGiftLine) {
      if (window.console) console.log('[auto-add-free-gift] trigger present, free gift missing -> add');
      addFreeGiftLine();
    } else if (triggerCount === 0 && freeGiftLine) {
      if (window.console) console.log('[auto-add-free-gift] no trigger, free gift present -> remove');
      removeFreeGift(freeGiftLine);
    }
  }

  function variantFromEvent(event) {
    var product = event && event.detail && event.detail.product;
    if (!product) return null;
    var id = Number(product.variant_id || product.id);
    return id || null;
  }

  // Instant add on a successful AJAX add-to-cart.
  document.addEventListener('product-ajax:added', function (event) {
    var vid = variantFromEvent(event);
    if (!vid) return;
    if (!isCollectionVariant(vid)) return; // only act for collection products (incl. the gift)
    if (window.console) console.log('[auto-add-free-gift] trigger product added', vid);
    ensureGiftInCart();
  });

  // Reconcile from a fresh server cart (no-cache) so we never act on a stale
  // event snapshot — a removal publishes a `cartUpdate` whose cart object can
  // still show the just-removed line, which would wrongly skip the re-add.
  // Debounced because cart changes often fire several events in quick succession.
  var reconcileTimer = null;
  function scheduleReconcile() {
    if (reconcileTimer) clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(function () {
      reconcileTimer = null;
      getCart().then(reconcileFromCart);
    }, 200);
  }

  // Reactive reconcile after any cart change (add, remove, quantity update).
  // The theme signals cart changes via the `cartUpdate` pub/sub event and the
  // `cart:refresh` / `cart:updated` document events; hook all of them.
  if (PUB_SUB && CART_UPDATE) {
    PUB_SUB.subscribe(CART_UPDATE, scheduleReconcile);
  }
  document.addEventListener('cart:refresh', scheduleReconcile);
  document.addEventListener('cart:updated', scheduleReconcile);

  // Reconcile once on load in case the cart already violates the invariant
  // (e.g. the free line was removed on another page/device).
  getCart().then(reconcileFromCart);
})();
